import { useEffect, useState } from 'react';
import {
  Box,
  Link,
  List,
  ListItem,
  ListItemText,
  Theme,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from 'tss-react/mui';

import { ChangelogEntry, getFullChangelog } from '../../api/versionsApi/versionsApi';
import Loader from '../../components/Loader';

const hideViewCommit = true;

const useStyles = makeStyles()((theme: Theme) => ({
  page: {
    minHeight: '100vh',
    backgroundColor: theme.palette.primary.light,
    padding: '40px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  container: {
    width: '100%',
    maxWidth: '720px',
    padding: '0 24px',
  },
  pageTitle: {
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: '22px',
    color: theme.palette.common.white,
    marginBottom: '24px',
  },
  versionBlock: {
    marginBottom: '12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(118, 120, 133, 0.35)',
    borderRadius: '10px',
    padding: '16px 18px',
  },
  versionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    paddingBottom: '10px',
    borderBottom: '1px solid rgba(118, 120, 133, 0.35)',
  },
  versionNumber: {
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: '15px',
    color: theme.palette.common.white,
  },
  versionDate: {
    fontFamily: 'Inter',
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  typeBadge: {
    fontFamily: 'Inter',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: '4px',
    padding: '2px 6px',
    color: theme.palette.common.white,
  },
  sectionLabel: {
    fontFamily: 'Inter',
    fontSize: '11px',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginTop: '10px',
    marginBottom: '4px',
  },
  sectionBlock: {
    backgroundColor: 'rgba(0,0,0,0.15)',
    borderRadius: '6px',
    padding: '8px 12px',
    marginBottom: '4px',
  },
  changeList: {
    padding: '0 0 0 8px',
    margin: 0,
  },
  changeItem: {
    padding: '2px 0',
    '& .MuiListItemText-primary': {
      fontFamily: 'Inter',
      fontSize: '13px',
      color: theme.palette.common.white,
    },
  },
  commitLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: theme.palette.text.secondary,
    marginTop: '4px',
    fontFamily: 'Inter',
  },
  noChanges: {
    fontFamily: 'Inter',
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  stateText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: '48px 0',
  },
}));

const ChangelogFullView = () => {
  const { classes } = useStyles();
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getFullChangelog()
      .then((data) => setEntries(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box className={classes.page}>
      <Box className={classes.container}>
        <Typography className={classes.pageTitle}>Version History</Typography>

        {loading && <Loader colorType="warning" size={48} />}

        {!loading && error && (
          <Typography className={classes.stateText}>Failed to load version history.</Typography>
        )}

        {!loading && !error && entries.length === 0 && (
          <Typography className={classes.stateText}>No entries found.</Typography>
        )}

        {!loading &&
          !error &&
          entries.map((entry) => (
            <Box key={entry.version} className={classes.versionBlock}>
              <Box className={classes.versionHeader}>
                <Typography className={classes.versionNumber}>{entry.version}</Typography>
                <Typography className={classes.typeBadge}>{entry.type}</Typography>
                <Typography className={classes.versionDate}>{entry.date}</Typography>
              </Box>

              {/* Changes */}
              <Typography className={classes.sectionLabel}>Changes</Typography>
              <Box className={classes.sectionBlock}>
                {entry.changes.length > 0 ? (
                  <List className={classes.changeList} disablePadding>
                    {entry.changes.map((change, i) => (
                      <ListItem key={i} className={classes.changeItem} disableGutters disablePadding>
                        <ListItemText primary={`• ${change}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography className={classes.noChanges}>No changes</Typography>
                )}
                {!hideViewCommit && entry.commit && (
                  <Link
                    href={entry.commit}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.commitLink}
                    underline="hover"
                  >
                    View commit <OpenInNewIcon sx={{ fontSize: '11px' }} />
                  </Link>
                )}
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default ChangelogFullView;
